import {
  Alert,
  Box,
  Button,
  Card,
  Fade,
  FormControl,
  FormControlLabel,
  Grid,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Modal,
  Select,
  Snackbar,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import MDTypography from "components/MDTypography";
import AddANote from "components/AddANote";
import useDropdowns from "hooks/useDropdowns";
import TaskDetailsModal from "layouts/pages/tasks/components/TaskDetailsModal";
import { useEffect, useMemo, useState } from "react";
import { useAppContextController } from "context/AppContext";
import { ThumbDown, ThumbUp } from "@mui/icons-material";
import FlexWrapper from "components/Wrapper/FlexWrapper";
import MDEditor from "components/MDEditor";
import MDBox from "components/MDBox";
import { useMutation } from "react-query";
import updateInterviewFeedback from "layouts/pages/events/actions/updateInterviewFeedback";
import { useSnackbar } from "notistack";
import { removeStyleAttribute } from "utils/string";
import ConfirmationModal from "components/ConfirmationModal";
import ConfirmModal from "components/ConfirmDialog/ConfirmModal";

const useStyle = makeStyles({
  container: {
    position: "absolute",
    width: "25%",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "2rem",
  },
  wideContainer: {
    width: "50%",
  },
  dropzone: {
    height: 170,
  },
  editor: {
    height: 150,
  },
});

const ChangeInterviewFeedbackModal = ({
  open,
  handleClose,
  applicant,
  user,
  eventUrl,
  onChangeFeedback,
  refetchApplicants,
  disableFinal,
}) => {
  const { currentLoggedUser } = useAppContextController();
  const { enqueueSnackbar } = useSnackbar();
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const [currentRating, currentFinalRating] = useMemo(() => {
    const currentInterview = (applicant?.interviews ?? []).find((int) => int.eventUrl === eventUrl);
    const finalRating = currentInterview?.feedback?.rating ?? "None";

    const currentFeedback = (applicant?.feedback ?? []).find((fb) =>
      user
        ? fb.createAgent === user?.id || fb.createAgent === user?._id
        : fb.createAgent === currentLoggedUser?._id
    );
    const rating = currentFeedback?.rating ?? "None";
    return [rating, finalRating];
  }, [applicant?.feedback, applicant?.interviews, currentLoggedUser?._id, eventUrl, user]);

  const [rating, setRating] = useState(currentRating ?? "None");
  const [isFinalDecision, setIsFinalDecision] = useState(false);
  const [editorValue, setEditorValue] = useState("");

  const canSetFinalDecision = !user || currentLoggedUser?._id === user?._id;

  const classes = useStyle();

  const updateInterviewFeedbackMutation = useMutation(updateInterviewFeedback, {
    onError: () => enqueueSnackbar("Something went wrong!", { variant: "error" }),
    onSuccess: async (_, data) => {
      enqueueSnackbar("Interview feedback has been updated!", { variant: "success" });
      refetchApplicants?.();
    },
  });

  const handleUpdateFeedback = async (data) => {
    await updateInterviewFeedbackMutation.mutateAsync({
      applicantId: applicant?.id || applicant?._id,
      eventUrl,
      data,
    });
    if (onChangeFeedback)
      onChangeFeedback({
        applicant,
        eventUrl,
        rating: data.feedback.rating,
        note: data.note,
        isFinal: data.isFinal,
      });
  };

  const handleFeedback = () => {
    const strippedNote = removeStyleAttribute(editorValue);
    const note =
      canSetFinalDecision && strippedNote
        ? {
            type: "Interview Note",
            eventUrl,
            text: editorValue,
            firstName: currentLoggedUser?.firstName,
            lastName: currentLoggedUser?.lastName,
            createAgent: currentLoggedUser?._id,
            date: new Date().toISOString(),
            userId: currentLoggedUser?._id,
          }
        : undefined;

    const currentUser = user ?? {
      _id: currentLoggedUser?._id,
      firstName: currentLoggedUser?.firstName,
      lastName: currentLoggedUser?.lastName,
    };

    const newFeedback = {
      createAgent: currentUser?._id,
      agent: `${currentUser?.firstName} ${currentUser?.lastName}`,
      firstName: currentUser?.firstName,
      lastName: currentUser?.lastName,
      eventUrl,
      modifiedDate: new Date().toISOString(),
      rating,
    };
    handleUpdateFeedback({ feedback: newFeedback, isFinal: isFinalDecision, note });
    handleClose();
  };

  useEffect(() => {
    if (open) {
      setRating(currentRating ?? "None");
      setIsFinalDecision(false);
      setEditorValue("");
    }
  }, [currentRating, open]);

  const handleChangeType = (e, newValue) => {
    if (newValue) {
      setRating(currentFinalRating);
    } else {
      setRating(currentRating);
    }
    setIsFinalDecision(newValue);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Fade in={open}>
          <Card className={classes.container}>
            <MDTypography variant="h5" color="dark">
              Add Feedback
            </MDTypography>
            <Grid container>
              <Grid container item xs={6}>
                <Grid container xs={12} sx={{ width: "100%", mt: 2, gap: 1 }}>
                  <MDTypography variant="h6" color="dark">
                    Applicant:
                  </MDTypography>
                  <MDTypography variant="h6" color="info">
                    {`${applicant?.firstName} ${applicant?.lastName}`}
                  </MDTypography>
                </Grid>
                {!!user && !canSetFinalDecision && (
                  <Grid container xs={12} sx={{ width: "100%", gap: 1 }}>
                    <MDTypography variant="h6" color="dark">
                      User:
                    </MDTypography>
                    <MDTypography variant="h6" color="info">
                      {`${user.firstName} ${user.lastName}`}
                    </MDTypography>
                  </Grid>
                )}
                <Grid container xs={12} sx={{ width: "100%", mt: 2 }}>
                  <Grid item xs={12}>
                    <Select
                      value={rating}
                      onChange={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setRating(e.target.value);
                      }}
                      sx={{ width: "100%", padding: 0.5 }}
                    >
                      <MenuItem value="None">
                        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                          <ListItemIcon>
                            <ThumbUp fontSize="small" color="dark" />
                          </ListItemIcon>
                          <ListItemText>None</ListItemText>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Yes">
                        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                          <ListItemIcon>
                            <ThumbUp fontSize="small" color="success" />
                          </ListItemIcon>
                          <ListItemText>Yes</ListItemText>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Maybe">
                        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                          <ListItemIcon>
                            <ThumbUp fontSize="small" color="warning" />
                          </ListItemIcon>
                          <ListItemText>Maybe</ListItemText>
                        </Box>
                      </MenuItem>
                      <MenuItem value="No">
                        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                          <ListItemIcon>
                            <ThumbUp fontSize="small" color="error" />
                          </ListItemIcon>
                          <ListItemText>No</ListItemText>
                        </Box>
                      </MenuItem>
                    </Select>
                  </Grid>
                </Grid>
              </Grid>
              {canSetFinalDecision && !disableFinal && (
                <Grid
                  container
                  item
                  xs={6}
                  sx={{ alignItems: "flex-end", justifyContent: "center" }}
                >
                  <ToggleButtonGroup
                    color="info"
                    orientation="vertical"
                    value={isFinalDecision}
                    exclusive
                    onChange={handleChangeType}
                  >
                    <ToggleButton value={false} aria-label="list">
                      Personal Feedback
                    </ToggleButton>
                    <ToggleButton value aria-label="module">
                      Final Decision
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              )}
            </Grid>
            {canSetFinalDecision && (
              <Grid container xs={12} sx={{ width: "100%", mt: 2 }}>
                <MDBox sx={{ height: "275px" }}>
                  <MDTypography variant="body2" color="dark">
                    NOTE
                  </MDTypography>
                  <MDEditor
                    className={classes.editor}
                    value={editorValue}
                    onChange={(e) => setEditorValue(e)}
                  />
                </MDBox>
              </Grid>
            )}
            <Grid container spacing={3} justifyContent="flex-end" mt={1}>
              <Grid item sm={3}>
                <Button
                  color="error"
                  variant="contained"
                  fullWidth
                  onClick={(e) => {
                    e?.stopPropagation();
                    handleClose();
                  }}
                >
                  <MDTypography variant="body2" color="white">
                    Cancel
                  </MDTypography>
                </Button>
              </Grid>
              <Grid item sm={3}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={async (e) => {
                    e?.stopPropagation();
                    if (isFinalDecision) {
                      setIsConfirmationOpen(true);
                    } else {
                      handleFeedback();
                    }
                  }}
                >
                  <MDTypography variant="body2" color="white">
                    Save
                  </MDTypography>
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Fade>
      </Modal>
      <ConfirmModal
        isOpen={isConfirmationOpen}
        closeFn={() => setIsConfirmationOpen(false)}
        title="Final Decision"
        description="Are you sure you want to set the final decision for this applicant?"
        positiveBtn={{
          label: "Confirm",
          fn: (e) => {
            e.stopPropagation();
            setIsConfirmationOpen(false);
            handleFeedback();
          },
          isShown: true,
        }}
        negativeBtn={{
          label: "Cancel",
          fn: (e) => {
            e.stopPropagation();
            setIsConfirmationOpen(false);
          },
          isShown: true,
        }}
      />
    </>
  );
};

export default ChangeInterviewFeedbackModal;
